import CallIcon from "@mui/icons-material/Call";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { linko } from "@shared/linko";
import { type FC } from "react";
import { useAlert } from "../../contexts/AlertProvider";

function errorMessage(error: unknown): string {
  const ax = error as AxiosError<{ message?: string | string[]; }>;
  const m = ax.response?.data?.message;
  if (typeof m === "string") return m;
  if (Array.isArray(m)) return m.join(", ");
  if (ax.message && ax.response === undefined) return ax.message;
  return "לא ניתן ליזום שיחה";
}

export interface VoicenterCallButtonProps {
  phoneNumber: string;
  /** Smaller icon (e.g. tooltips, dense rows) */
  compact?: boolean;
  onCallInitiated?: () => void;
}

export const VoicenterCallButton: FC<VoicenterCallButtonProps> = ({
  phoneNumber,
  compact,
  onCallInitiated,
}) => {
  const { setAlertText, setSeverity, setTime } = useAlert();

  const { data: cfg } = useQuery({
    queryKey: ["voicenter-click2call-config"],
    queryFn: async () =>
      (await linko.voicenter.getClick2CallConfiguration(undefined)).data,
    staleTime: 1000 * 90,
    retry: 1,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      linko.voicenter.initiateClick2Call({ targetPhone: phoneNumber }),
    onSuccess: () => {
      onCallInitiated?.();
      setSeverity("success");
      setAlertText("שיחת קליק־טו־קול נשלחה - עמדת הנציג אמורה לצלצל אליך");
      setTime(3500);
    },
    onError: (error: unknown) => {
      setSeverity("error");
      setAlertText(errorMessage(error));
      setTime(4000);
    },
  });

  if (!phoneNumber?.trim() || !cfg?.configured) {
    return null;
  }

  return (
    <Tooltip title="התקשר (Voicenter)">
      <span className={compact ? "voicenter-call-button voicenter-call-button--compact" : "voicenter-call-button"}>
        <IconButton
          type="button"
          size={compact ? "small" : "medium"}
          disabled={isPending}
          onClick={(e) => {
            e.stopPropagation();
            mutate();
          }}
          aria-label="התקשר ב-Voicenter"
          sx={compact ? { padding: "2px" } : undefined}
        >
          {isPending ? (
            <CircularProgress size={compact ? 14 : 20} />
          ) : (
            <CallIcon
              sx={{ fontSize: compact ? "1.1rem" : "1.35rem", color: "primary.main" }}
            />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};
